import React, { useContext, useEffect, useRef } from 'react';
import { Icon } from '@iconify/react';
import { CircularProgress } from '@mui/material';
import { TextsContext } from '../../../context/textsContext';
import DialogComponent from '../../../components/DialogComponent';
import { TitleH2Component } from '../../../components/TitleH2Component';
import { ParagraphComponent } from '../../../components/ParagraphComponent';
import style from '../../../scss/dashboard.module.scss';

const AUTO_CLOSE_MS = 5000;

/**
 * Diálogo de eliminación de un plan favorito con 4 estados derivados de la mutación:
 *   confirm  → pregunta de confirmación (Accept / Cancel)
 *   loading  → mientras se elimina; sin acciones ni "X" (no se puede cerrar)
 *   success  → confirmación de éxito; se auto-cierra a los 5s
 *   error    → falló; permite reintentar (Retry / Cancel)
 *
 * `onClose` debe reiniciar el estado en el padre (setPlanToDelete(null) + mutation.reset()).
 */
const DeletePlanDialog = ({ show, isLoading, isError, isSuccess, onConfirm, onClose }) => {
    const { texts } = useContext(TextsContext);

    const status = isSuccess ? 'success' : isError ? 'error' : isLoading ? 'loading' : 'confirm';

    // Mantiene la última referencia de onClose sin re-disparar el timer (onClose puede
    // recrearse en cada render del padre).
    const onCloseRef = useRef(onClose);
    useEffect(() => { onCloseRef.current = onClose; });

    // Auto-cierra tras 5s en éxito (si el usuario no lo cierra antes). El timer se fija
    // una sola vez al entrar en "success" y solo se limpia al cambiar de estado.
    useEffect(() => {
        if (status !== 'success') return undefined;
        const timer = setTimeout(() => { onCloseRef.current?.(); }, AUTO_CLOSE_MS);
        return () => clearTimeout(timer);
    }, [status]);

    // Acciones/botones del diálogo según el estado. En "loading" no se pasa ningún
    // handler, así el DialogComponent no muestra footer ni "X" → no se puede cerrar.
    const actionsByStatus = {
        confirm: {
            onConfirm,
            onCancel: onClose,
            onClose,
            confirmText: texts?.dashboard_delete_confirm ?? 'Accept',
            cancelText: texts?.dashboard_delete_cancel ?? 'Cancel',
        },
        loading: {},
        success: {
            onConfirm: onClose,
            onClose,
            confirmText: texts?.dashboard_delete_close ?? 'Close',
        },
        error: {
            onConfirm,
            onCancel: onClose,
            onClose,
            confirmText: texts?.dashboard_delete_retry ?? 'Retry',
            cancelText: texts?.dashboard_delete_cancel ?? 'Cancel',
        },
    };

    const titleClass = `raleway fw_700 color_primary ${style.deleteDialogTitle}`;
    const textClass = `Poppins ${style.deleteDialogText}`;

    return (
        <DialogComponent show={show} width="500px" {...actionsByStatus[status]}>
            {status === 'confirm' && (
                <>
                    <Icon icon="mdi:trash-can-outline" className={style.deleteDialogIcon} />
                    <TitleH2Component styleGroup={titleClass}>
                        {texts?.dashboard_delete_title ?? 'Are you sure you want to delete?'}
                    </TitleH2Component>
                    <ParagraphComponent styleGroup={textClass}>
                        {texts?.dashboard_delete_text ?? 'This action cannot be undone.'}
                    </ParagraphComponent>
                </>
            )}

            {status === 'loading' && (
                <>
                    <div className="mb-3 d-flex justify-content-center">
                        <CircularProgress size={40} style={{ color: '#002855' }} />
                    </div>
                    <TitleH2Component styleGroup={titleClass}>
                        {texts?.dashboard_delete_loading_title ?? 'Deleting…'}
                    </TitleH2Component>
                    <ParagraphComponent styleGroup={textClass}>
                        {texts?.dashboard_delete_loading_text ?? 'Please wait while we delete your plan.'}
                    </ParagraphComponent>
                </>
            )}

            {status === 'success' && (
                <>
                    <Icon icon="mdi:check-circle-outline" width={40} height={40} style={{ color: '#61A60E' }} className={style.deleteDialogIcon} />
                    <TitleH2Component styleGroup={titleClass}>
                        {texts?.dashboard_delete_success_title ?? 'Plan deleted'}
                    </TitleH2Component>
                    <ParagraphComponent styleGroup={textClass}>
                        {texts?.dashboard_delete_success_text ?? 'The plan has been deleted successfully.'}
                    </ParagraphComponent>
                </>
            )}

            {status === 'error' && (
                <>
                    <Icon icon="mdi:alert-circle-outline" width={40} height={40} style={{ color: '#F12B24' }} className={style.deleteDialogIcon} />
                    <TitleH2Component styleGroup={titleClass}>
                        {texts?.dashboard_delete_error_title ?? 'Something went wrong'}
                    </TitleH2Component>
                    <ParagraphComponent styleGroup={textClass}>
                        {texts?.dashboard_delete_error_text ?? 'We couldn’t delete the plan. Please try again.'}
                    </ParagraphComponent>
                </>
            )}
        </DialogComponent>
    );
};

export default DeletePlanDialog;
